import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ExternalLink, CalendarIcon, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { isStripeTestMode } from "@/config/stripePlans";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { format, startOfDay, parse, addHours } from "date-fns";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { BookingAccessRecovery } from "@/components/booking/BookingAccessRecovery";

// Booking times come in two formats: session bookings store 12-hour ("4:00 PM"),
// appointment bookings store 24-hour ("11:00"). Parse robustly so date/time
// guards (past-slot, <24h) apply consistently to BOTH booking types.
const parseBookingDateTime = (date: string, time: string): Date => {
  let dt = parse(`${date} ${time}`, "yyyy-MM-dd h:mm a", new Date());
  if (isNaN(dt.getTime())) dt = parse(`${date} ${time}`, "yyyy-MM-dd HH:mm", new Date());
  return dt;
};

const ReadOnlyField = ({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <Input value={value ?? "-"} readOnly disabled />
  </div>
);

const CancelBooking = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get("token") ?? "";

  const [booking, setBooking] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pastDateModalOpen, setPastDateModalOpen] = useState(false);
  const [lessThan24hModalOpen, setLessThan24hModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isPassed, setIsPassed] = useState(false);
  const [isWithin24h, setIsWithin24h] = useState(false);
  const [scope, setScope] = useState<"one" | "following" | "all">("one");
  const [seriesTotal, setSeriesTotal] = useState<number | null>(null);
  // For series bookings: siblings that are >24h away and thus cancellable
  const [hasCancellableSiblings, setHasCancellableSiblings] = useState(false);

  useEffect(() => {
    const fetchBooking = async () => {
      if (!bookingId) {
        setError("Invalid booking ID");
        setLoading(false);
        return;
      }

      // Booking + series info come from the manage-booking fn. The bookings
      // table is no longer publicly readable.
      const { data: lookup, error } = await supabase.functions.invoke("manage-booking", {
        body: { action: "get", purpose: "cancel", access_token: accessToken },
      });
      const data = lookup?.booking;

      if (error || !data) {
        setError("Booking not found");
      } else {
        setBooking(data);

        // If this is part of a series, the lookup includes the total occurrence
        // count so we can explain scope choices to the customer.
        if (lookup.series?.total_occurrences != null) {
          setSeriesTotal(lookup.series.total_occurrences);
        }

        // Validation on page load
        if (data.status !== 'cancelled') {
          try {
            const bookingDateTime = parseBookingDateTime(data.booking_date, data.booking_time);
            const now = new Date();
            
            if (bookingDateTime < now) {
              setIsPassed(true);
              setPastDateModalOpen(true);
            } else if (bookingDateTime < addHours(now, 24)) {
              setIsWithin24h(true);
              // For series bookings, check if there are other cancellable siblings
              // before showing the blocking modal.
              if ((data as any).series_id) {
                const siblings = (lookup.series?.occurrences || []).filter(
                  (s: any) => s.status !== "cancelled" && s.booking_id !== data.booking_id
                );
                const twentyFourHoursLater = addHours(now, 24);
                const cancellableSiblings = (siblings || []).filter((s: any) => {
                  try {
                    const sibDateTime = parseBookingDateTime(s.booking_date, s.booking_time);
                    return sibDateTime >= twentyFourHoursLater;
                  } catch { return false; }
                });
                if (cancellableSiblings.length > 0) {
                  setHasCancellableSiblings(true);
                  // Auto-select "all" since "one" is disabled and we show a
                  // single "Cancel all remaining sessions" option
                  setScope("all");
                  // Inform the user this row can't be cancelled individually
                  // but future weeks can be.
                  setLessThan24hModalOpen(true);
                } else {
                  setLessThan24hModalOpen(true);
                }
              } else {
                setLessThan24hModalOpen(true);
              }
            }
          } catch (e) {
            console.error("Error parsing booking date and time:", e);
          }
        }
      }
      setLoading(false);
    };

    fetchBooking();
  }, [bookingId, accessToken]);

  if (!accessToken) return <BookingAccessRecovery bookingId={bookingId} />;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Loading booking details...</p>
      </div>
    );
  }

  if (error || !booking) return <BookingAccessRecovery bookingId={bookingId} />;

  const { listings } = booking;
  const imageUrl = listings?.images && listings.images.length > 0 ? listings.images[0] : null;

  // Format date for display (dd/MM/yyyy)
  const formatDisplayDate = (dateStr: string) => {
    const parts = dateStr.split("-");
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateStr;
  };

  // For series bookings where the current row is within 24h, the customer
  // can still cancel future siblings via "following" or "all" scope.
  const isCancelBlocked = (() => {
    if (booking.status === 'cancelled' || isPassed) return true;
    if (!isWithin24h) return false;
    // Within 24h. Block "this only", but allow wider scopes if there are
    // cancellable future siblings.
    if (booking.series_id && hasCancellableSiblings && scope !== 'one') return false;
    return true;
  })();

  const handleValidationCheck = () => {
    if (isCancelBlocked) return;
    setConfirmModalOpen(true);
  };

  const confirmCancellation = async () => {
    setIsCancelling(true);

    const isStripe = booking.payment_method === 'stripe';
    const isSeries = !!booking.series_id;
    const seriesScope = isSeries ? scope : "one";
    let refundProcessingFailed = false;
    // For series cancels, refund-booking returns the full cascade list so
    // we can fire ONE summary email instead of N webhook-driven ones.

    // Compute the cancel notes UP FRONT so we can pass them to refund-booking,
    // which now owns the atomic { Stripe refund } + { status='cancelled' } +
    // { notes = cancelNotes } update on the server side. Previously the
    // cancel page did the row update AFTER refund-booking returned. If that
    // separate UPDATE failed, the customer was refunded but the row showed
    // 'confirmed'. Now there's a single source of truth.
    let cancelNotes: string;
    if (isSeries && seriesScope !== "one") {
      cancelNotes = seriesScope === "all"
        ? "Customer cancelled the entire recurring series. Charged occurrences refunded; uncharged future weeks dropped."
        : "Customer cancelled this and all following occurrences in the series. Future uncharged weeks dropped; this row refunded if it was already charged.";
    } else {
      cancelNotes = isStripe
        ? "Money has been processed for refunding (minus Stripe processing fees) and will be sent to the customer soon"
        : "No money needs to be refunded since the user chose pay at venue and haven't paid yet";
    }

    // Single entry point: refund-booking does the refund (if applicable) AND
    // the row status flip + notes write, atomically server-side. For manual /
    // pending-series rows it just does the row update. For Stripe charged
    // rows it does refund + row update. For cascade scopes it does refund (if
    // early-bird) + cancels the cascade targets + sets notes on the click row.
    let invokeError: unknown = null;
    let invokeData: any = null;
    if (isSeries && seriesScope !== "one") {
      const { data, error } = await supabase.functions.invoke("refund-booking", {
        body: {
          bookingId: booking.booking_id,
          access_token: accessToken,
          test_mode: isStripeTestMode,
          scope: seriesScope,
          cancelNotes,
        },
      });
      invokeError = error; invokeData = data;
    } else {
      const { data, error } = await supabase.functions.invoke("refund-booking", {
        body: {
          bookingId: booking.booking_id,
          access_token: accessToken,
          test_mode: isStripeTestMode,
          scope: "one",
          cancelNotes,
        },
      });
      invokeError = error; invokeData = data;
    }

    if (invokeError || !invokeData?.success) {
      console.error("refund-booking failed:", invokeError || invokeData);
      refundProcessingFailed = true;
    }

    setIsCancelling(false);

    // From here on we trust refund-booking. If it returned success the row
    // is already status='cancelled' on the server. If it failed, surface the
    // error to the customer without claiming the booking was cancelled.
    if (!refundProcessingFailed) {
      setConfirmModalOpen(false);
      setSuccessModalOpen(true);
      setBooking((prev: any) => ({ 
        ...prev, 
        status: isWithin24h ? prev.status : "cancelled", 
        notes: cancelNotes 
      }));
    } else {
      console.error("Failed to mark booking as cancelled:", invokeError || invokeData);
      toast.error("Couldn't cancel this booking right now — please try again, or contact the business directly if this keeps happening.");
    }
  };

  const businessSlug = listings?.businesses?.slug;
  const listingLink = booking.listing_id
    ? `/listing/${booking.listing_id}`
    : "#";

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-center">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/dibs_assets/red_logo_with_transparent_bg.png"
              alt="Dibs"
              className="h-8 object-contain"
            />
            <span className="text-primary font-sans font-bold text-2xl tracking-tight">Dibs</span>
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 md:py-12 flex flex-col gap-6">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
              <AlertCircle className="w-5 h-5 text-destructive" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground leading-none">Cancel booking</h1>
          </div>
          <p className="text-muted-foreground mt-1 text-sm">Review your booking details below. Are you sure you want to cancel this reservation?</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-start">
          {/* LEFT: Listing Card */}
          <div className="md:col-span-2 border rounded-xl shadow-sm bg-card overflow-hidden">
            {imageUrl ? (
              <div className="aspect-video w-full bg-muted">
                <img src={imageUrl} alt={listings?.name} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="aspect-video w-full bg-muted flex items-center justify-center">
                <span className="text-muted-foreground text-sm">No image available</span>
              </div>
            )}

            <div className="p-5 flex flex-col gap-3">
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-lg font-bold leading-tight flex-1">{listings?.name}</h2>
                <Link
                  to={listingLink}
                  className="shrink-0 p-1.5 bg-muted/50 rounded-md hover:bg-muted text-muted-foreground hover:text-primary transition-colors"
                  aria-label="Open listing in new window"
                >
                  <ExternalLink className="w-4 h-4" />
                </Link>
              </div>

              {listings?.description && (
                <p className="text-sm text-muted-foreground line-clamp-4">
                  {listings.description}
                </p>
              )}
            </div>
          </div>

          {/* RIGHT: Booking Details */}
          <div className={`md:col-span-3 border rounded-xl shadow-xl bg-card overflow-hidden flex flex-col relative ${booking.status === 'cancelled' ? 'border-muted' : 'border-destructive/20'}`}>
            <div className="p-6 space-y-6 flex-1">
              {/* Schedule Display */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Schedule</h3>
                    {booking.status === 'cancelled' && (
                        <span className="px-2.5 py-1 uppercase text-[10px] font-bold tracking-widest rounded-full bg-destructive text-destructive-foreground">Cancelled</span>
                    )}
                </div>

                <div className="grid gap-4">
                  <div className="space-y-1.5">
                    <Label>Date</Label>
                    <Input value={formatDisplayDate(booking.booking_date)} readOnly disabled />
                  </div>

                  <div className="space-y-1.5">
                    <Label>Time</Label>
                    <Input value={booking.booking_time} readOnly disabled />
                  </div>
                </div>
              </div>

              {/* Series scope selector */}
              {booking.series_id && booking.status !== 'cancelled' && (
                <div className="space-y-3 rounded-lg border border-primary/20 bg-primary/5 p-4">
                  <div className="text-sm">
                    <span className="font-medium text-foreground">Part of a recurring series</span>
                    {seriesTotal && booking.series_position && (
                      <span className="text-muted-foreground ml-1">
                        ({booking.series_position} of {seriesTotal})
                      </span>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium">Apply cancellation to</Label>
                    {isWithin24h && hasCancellableSiblings ? (
                      /* When this session is within 24h, show simplified options:
                         "this only" is disabled, and "following"/"all" are effectively
                         identical (past sessions are excluded by backend), so merge
                         them into one clear option. */
                      <RadioGroup value={scope} onValueChange={(v) => setScope(v as "one" | "following" | "all")} className="space-y-1.5">
                        <div className="flex items-start gap-2">
                          <RadioGroupItem value="one" id="scope-one" className="mt-0.5" disabled />
                          <Label htmlFor="scope-one" className="font-normal cursor-pointer text-muted-foreground line-through">
                            This session only
                            <span className="text-xs text-destructive ml-1 no-underline inline-block" style={{ textDecoration: 'none' }}>(within 24h)</span>
                          </Label>
                        </div>
                        <div className="flex items-start gap-2">
                          <RadioGroupItem value="all" id="scope-all" className="mt-0.5" />
                          <Label htmlFor="scope-all" className="font-normal cursor-pointer">
                            Cancel all remaining sessions
                            <span className="text-xs text-muted-foreground ml-1">(excludes this session)</span>
                          </Label>
                        </div>
                      </RadioGroup>
                    ) : (
                      <RadioGroup value={scope} onValueChange={(v) => setScope(v as "one" | "following" | "all")} className="space-y-1.5">
                        <div className="flex items-start gap-2">
                          <RadioGroupItem value="one" id="scope-one" className="mt-0.5" />
                          <Label htmlFor="scope-one" className="font-normal cursor-pointer">This booking only</Label>
                        </div>
                        <div className="flex items-start gap-2">
                          <RadioGroupItem value="following" id="scope-following" className="mt-0.5" />
                          <Label htmlFor="scope-following" className="font-normal cursor-pointer">This and all following bookings</Label>
                        </div>
                        <div className="flex items-start gap-2">
                          <RadioGroupItem value="all" id="scope-all" className="mt-0.5" />
                          <Label htmlFor="scope-all" className="font-normal cursor-pointer">The whole series</Label>
                        </div>
                      </RadioGroup>
                    )}
                  </div>
                  {isWithin24h && hasCancellableSiblings && (
                    <p className="text-xs text-amber-600 font-medium">
                      ⚠ Today's session ({booking.booking_time}) is within 24 hours and will not be cancelled.
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Refunds only apply to occurrences already charged. Future uncharged weeks are dropped with no Stripe action. Past sessions stay as-is.
                  </p>
                </div>
              )}

              {/* Guest Information Display */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Guest information</h3>
                <div className="grid gap-4 sm:grid-cols-2 opacity-70">
                  <ReadOnlyField
                    label="Name"
                    value={booking.customer_name}
                  />
                  <ReadOnlyField
                    label="Pax"
                    value={booking.number_of_pax}
                  />
                  <ReadOnlyField
                    label="Email"
                    value={booking.customer_email}
                  />
                  {booking.customer_phone && (
                    <ReadOnlyField
                      label="Phone"
                      value={booking.customer_phone}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="px-6 pb-6 pt-0 flex flex-col sm:flex-row items-center justify-end gap-3 mt-auto">
              <Button
                variant="outline"
                onClick={() => navigate(businessSlug ? `/${businessSlug}` : "/")}
                className="w-full sm:w-auto"
              >
                Back to home
              </Button>
              <Button 
                onClick={handleValidationCheck} 
                variant="destructive" 
                className="w-full sm:w-auto"
                disabled={isCancelBlocked}
              >
                {booking.status === 'cancelled' ? 'Already cancelled' : 'Confirm cancellation'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* LESS THAN 24H MODAL */}
      <Dialog open={lessThan24hModalOpen} onOpenChange={setLessThan24hModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{hasCancellableSiblings ? 'This Session Cannot Be Cancelled' : 'Cancellation Unavailable'}</DialogTitle>
            <DialogDescription>
              {hasCancellableSiblings ? (
                <>
                  This booking is less than 24 hours away and cannot be cancelled individually.
                  <br /><br />
                  However, you can still cancel <strong>future sessions</strong> in this recurring series by choosing "Cancel all remaining sessions" below. Only sessions more than 24 hours away will be cancelled.
                </>
              ) : (
                <>
                  Bookings cannot be cancelled less than 24 hours prior to the booking timeslot.
                  <br /><br />
                  For more enquiries, contact the business at <a href={`mailto:${booking?.listings?.businesses?.email}`} className="text-primary hover:underline">{booking?.listings?.businesses?.email}</a>.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-start">
            <Button type="button" variant="outline" onClick={() => setLessThan24hModalOpen(false)}>
              {hasCancellableSiblings ? 'Got it' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PASTD DATE MODAL */}
      <Dialog open={pastDateModalOpen} onOpenChange={setPastDateModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancellation Unavailable</DialogTitle>
            <DialogDescription>
              This booking cannot be cancelled because the booking date has already passed. You may only cancel upcoming bookings.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-start">
            <Button type="button" variant="outline" onClick={() => setPastDateModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CONFIRM CANCELLATION MODAL */}
      <Dialog open={confirmModalOpen} onOpenChange={setConfirmModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Cancellation</DialogTitle>
            <DialogDescription className="space-y-2">
              <p>
                {(() => {
                  if (booking.series_id) {
                    if (scope === 'all') {
                      return isWithin24h 
                        ? "Are you sure you want to cancel all remaining sessions in this series? Today's session will not be cancelled. This action will not be reversible."
                        : "Are you sure you want to cancel the entire recurring series? This action will not be reversible.";
                    }
                    if (scope === 'following') {
                      return isWithin24h
                        ? "Are you sure you want to cancel all following sessions? Today's session will not be cancelled. This action will not be reversible."
                        : "Are you sure you want to cancel this and all following sessions in the series? This action will not be reversible.";
                    }
                    return "Are you sure you want to cancel this specific session? This action will not be reversible.";
                  }
                  return "Are you sure you want to cancel this booking? This action will not be reversible.";
                })()}
              </p>
              {booking.payment_method === 'stripe' ? (
                <p className="font-medium text-foreground">The money you paid minus the Stripe processing fee will be refunded back to you in 5 to 10 business days.</p>
              ) : Number(booking.total_paid || 0) <= 0 ? (
                <p className="font-medium text-foreground">This booking was free, so no refund is due.</p>
              ) : (
                <p className="font-medium text-foreground">No money will be refunded to you since you did not make any payment online (Pay at venue).</p>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setConfirmModalOpen(false)} disabled={isCancelling}>
              Close
            </Button>
            <Button type="button" variant="destructive" onClick={confirmCancellation} disabled={isCancelling}>
              {isCancelling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Confirm Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SUCCESS MODAL */}
      <Dialog open={successModalOpen} onOpenChange={setSuccessModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {(() => {
                if (booking.series_id) {
                  if (scope === 'all') return isWithin24h ? "Remaining Sessions Cancelled" : "Series Cancelled";
                  if (scope === 'following') return isWithin24h ? "Following Sessions Cancelled" : "Sessions Cancelled";
                  return "Booking Cancelled";
                }
                return "Booking Cancelled";
              })()}
            </DialogTitle>
            <DialogDescription>
              <span className="block mb-2">
                {(() => {
                  if (booking.series_id) {
                    if (scope === 'all') {
                      return isWithin24h 
                        ? "All remaining sessions in this series have been successfully cancelled. Today's session remains active."
                        : "The entire recurring series has been successfully cancelled.";
                    }
                    if (scope === 'following') {
                      return isWithin24h
                        ? "All following sessions have been successfully cancelled. Today's session remains active."
                        : "This and all following sessions in the series have been successfully cancelled.";
                    }
                    return "Your specific session has been successfully cancelled.";
                  }
                  return "Your booking has been successfully cancelled.";
                })()}
              </span>
              {booking.payment_method === 'stripe' && "The money you paid (minus processing fees) will be refunded back to you in 5 to 10 business days."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-start">
            <Button type="button" onClick={() => window.close()}>
              Close tab
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CancelBooking;
